'use client';

import { useCallback, useEffect, useState } from 'react';

import {
  DOCPARSER_VLM_HOP_IDS,
  type DocparserVlmHopId,
} from '@lib/docparser/types/docparser-vlm-hop-id';

export const UPLOAD_DOCPARSER_SETTINGS_KEY = 'deal-upload-docparser-settings';

export type UploadDocparserSettings = {
  /** Unset = follow the `enable-docparser` default (on). */
  useDocparserDetection?: boolean;
  /** Unset = full hop chain; set = staff pin from docparser-override-model UI. */
  docparserVlmHopOverride?: DocparserVlmHopId;
};

const DEFAULT_SETTINGS: UploadDocparserSettings = {};

function parseHopOverride(value: unknown): DocparserVlmHopId | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  return DOCPARSER_VLM_HOP_IDS.includes(value as DocparserVlmHopId)
    ? (value as DocparserVlmHopId)
    : undefined;
}

function parseStoredSettings(raw: string | null): UploadDocparserSettings {
  if (!raw) {
    return DEFAULT_SETTINGS;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<UploadDocparserSettings>;
    return {
      useDocparserDetection:
        typeof parsed.useDocparserDetection === 'boolean'
          ? parsed.useDocparserDetection
          : undefined,
      docparserVlmHopOverride: parseHopOverride(parsed.docparserVlmHopOverride),
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function readUploadDocparserSettings(): UploadDocparserSettings {
  if (typeof window === 'undefined') {
    return DEFAULT_SETTINGS;
  }

  return parseStoredSettings(
    sessionStorage.getItem(UPLOAD_DOCPARSER_SETTINGS_KEY)
  );
}

function writeUploadDocparserSettings(settings: UploadDocparserSettings): void {
  if (typeof window === 'undefined') {
    return;
  }

  sessionStorage.setItem(
    UPLOAD_DOCPARSER_SETTINGS_KEY,
    JSON.stringify(settings)
  );
}

export function useUploadDocparserSettings() {
  const [settings, setSettings] =
    useState<UploadDocparserSettings>(DEFAULT_SETTINGS);

  useEffect(() => {
    setSettings(readUploadDocparserSettings());
  }, []);

  const setUseDocparserDetection = useCallback((value: boolean) => {
    setSettings(current => {
      const next = {
        ...current,
        useDocparserDetection: value,
        ...(value ? {} : { docparserVlmHopOverride: undefined }),
      };
      writeUploadDocparserSettings(next);
      return next;
    });
  }, []);

  const setDocparserVlmHopOverride = useCallback(
    (value: DocparserVlmHopId | undefined) => {
      setSettings(current => {
        const next = { ...current, docparserVlmHopOverride: value };
        writeUploadDocparserSettings(next);
        return next;
      });
    },
    []
  );

  return {
    settings,
    setUseDocparserDetection,
    setDocparserVlmHopOverride,
  };
}
